import { useTranslation } from 'react-i18next';

import useAiChatToolLabel from '../../hooks/useAiChatToolLabel';
import useAiChatToolStatus from '../../hooks/useAiChatToolStatus';
import AiChatToolCard from '../AiChatToolCard';

import type { ReactElement, ReactNode } from 'react';
import type { IAiChatToolCallOf } from '../../interfaces/IAiChatToolCall';
import type { TDataFetchResult } from '../../interfaces/IAiChatTools';

import styles from '../../sass/AiChat.module.scss';

/**
 * The `fetchData` tool call: a data question answered with a statement, its rows stored.
 *
 * A step row, collapsed: the header names the call and shows the question; what the statement
 * made waits behind the toggle. The rows are never drawn here — `showDatasource` is where the
 * model places them — and a rejected statement is never printed: the reader gets the reason, the
 * SQL was written for the model.
 */
function FetchDataToolCall({ part }: IAiChatToolCallOf<'fetchData'>): ReactElement {
  const { t } = useTranslation();
  const label = useAiChatToolLabel('fetchData');
  const status = useAiChatToolStatus(part.state);

  const output = part.state === 'output-available' ? part.output : undefined;

  const readSummary = (result: TDataFetchResult): string => {
    switch (result.kind) {
      case 'clarification':
        return t('aiChat.tools.dataClarification');
      case 'rejected':
        return t('aiChat.tools.dataRejected');
      default:
        return t('aiChat.tools.dataRowsFetched', { count: result.rowCount });
    }
  };

  const readDetails = (result: TDataFetchResult): ReactNode => {
    switch (result.kind) {
      case 'clarification':
        return <p>{result.note}</p>;
      case 'rejected':
        return <p>{result.reason}</p>;
      default:
        return (
          <>
            <p>{result.purpose}</p>
            <p>{t('aiChat.tools.dataRowsStored', { count: result.rowCount })}</p>
            {result.kind === 'data' ? (
              <p>{t('aiChat.tools.dataRemainingRows', { count: result.remainingRows })}</p>
            ) : null}
            {result.warnings?.length ? (
              <ul className={styles.toolWarnings}>
                {result.warnings.map((warning) => <li key={warning}>{warning}</li>)}
              </ul>
            ) : null}
          </>
        );
    }
  };

  return (
    <AiChatToolCard
      name={label}
      status={output ? readSummary(output) : status}
      details={output ? readDetails(output) : undefined}
      errorText={part.errorText}
    >
      {part.input?.query}
    </AiChatToolCard>
  );
}

export default FetchDataToolCall;
